import { STATEMENT_TAXES_SUMMARY_FIELDS } from '../constants';

// The Statement of Taxes table balances the two columns with empty placeholder rows ({}). Those
// carry no category and must never appear in the audit diff — only real, labelled rows compare.
const isRealRow = (row) => !!(row && row.category && String(row.category).trim());

const isNumericLike = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'number') return true;
    return !isNaN(parseFloat(String(v).replace(/[(),]/g, '')));
};

// Normalize a money value for comparison. Tax can arrive as a parenthesised string — e.g.
// "(1,234.56)" means a negative amount — so strip the formatting and treat it as a number.
const normalizeForCompare = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string' && v.trim().startsWith('(')) {
        const n = parseFloat(v.replace(/[(),]/g, ''));
        return isNaN(n) ? v.trim() : (-Math.abs(n)).toFixed(2);
    }
    if (isNumericLike(v)) return parseFloat(String(v).replace(/,/g, '')).toFixed(2);
    return String(v).trim();
};

const rowKey = (row) => String(row.category).trim().toLowerCase();

// Total / summary rows the backend always appends to the end of each column. New data rows must
// land before these, exactly like the Total Paid / Total Received tables render them.
const TOTAL_ROW_KEYS = ['total paid', 'total received', 'taxes receivable (payable)'];
const isTotalRow = (category) => TOTAL_ROW_KEYS.includes(String(category).trim().toLowerCase());

// Columns compared/shown — mirrors STATEMENT_TAXES_SUMMARY_FIELDS so the preview reflects exactly
// the cells the Total Paid / Total Received tables render.
const CELL_CONFIG = STATEMENT_TAXES_SUMMARY_FIELDS;
const COMPARE_FIELDS = CELL_CONFIG.map((c) => c.id).filter((id) => id !== 'category');

const buildFields = (oldRow, newRow) => {
    const fields = {};
    CELL_CONFIG.forEach(({ id }) => {
        const oldVal = oldRow ? oldRow[id] : undefined;
        const newVal = newRow ? newRow[id] : undefined;
        const changed = COMPARE_FIELDS.includes(id) && !!oldRow && !!newRow
            && normalizeForCompare(oldVal) !== normalizeForCompare(newVal);
        fields[id] = { old: oldVal, new: newVal, changed };
    });
    return fields;
};

const buildSection = (key, title, oldRows, newRows) => {
    const oldList = (Array.isArray(oldRows) ? oldRows : []).filter(isRealRow);
    const newList = (Array.isArray(newRows) ? newRows : []).filter(isRealRow);

    const oldByKey = new Map();
    oldList.forEach((r) => oldByKey.set(rowKey(r), r));
    const newByKey = new Map();
    newList.forEach((r) => newByKey.set(rowKey(r), r));

    // Walk the NEW list in its display order — this is exactly what the table renders after a
    // refresh, so added rows naturally fall into place (before the Total rows).
    const rows = [];
    newList.forEach((newRow) => {
        const id = rowKey(newRow);
        const oldRow = oldByKey.get(id);
        const bold = isTotalRow(newRow.category);
        if (!oldRow) {
            rows.push({ id, category: newRow.category, status: 'added', bold, fields: buildFields(null, newRow) });
        } else {
            const fields = buildFields(oldRow, newRow);
            const changed = Object.values(fields).some((f) => f.changed);
            rows.push({ id, category: newRow.category, status: changed ? 'changed' : 'unchanged', bold, fields });
        }
    });

    // Removed rows (present in old, gone in new) keep their old order and slot in just before the
    // Total rows so they sit with the data, not after the totals.
    const removedRows = oldList
        .filter((oldRow) => !newByKey.has(rowKey(oldRow)))
        .map((oldRow) => ({
            id: rowKey(oldRow),
            category: oldRow.category,
            status: 'removed',
            bold: isTotalRow(oldRow.category),
            fields: buildFields(oldRow, null),
        }));
    if (removedRows.length) {
        const totalIdx = rows.findIndex((r) => isTotalRow(r.category));
        if (totalIdx === -1) rows.push(...removedRows);
        else rows.splice(totalIdx, 0, ...removedRows);
    }

    const counts = rows.reduce((acc, r) => {
        if (r.status !== 'unchanged') acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
    }, { changed: 0, added: 0, removed: 0 });

    return { key, title, cellConfig: CELL_CONFIG, rows, counts };
};

/**
 * Builds an audit diff between the currently-displayed Statement of Taxes (oldSummary) and the
 * recomputed-but-unsaved refresh result (newSummary). Returns one section per column so the dialog
 * can render the familiar Total Paid / Total Received layout side by side.
 *
 * @param {{paidList:Array, receivedList:Array}} oldSummary  Current rows from the store.
 * @param {{paidList:Array, receivedList:Array}} newSummary  Preview rows from the server.
 * @returns {{ sections: Array, counts: {changed:number, added:number, removed:number} }}
 */
export const buildStatementTaxesDiff = (oldSummary = {}, newSummary = {}) => {
    const paid = buildSection('paid', 'Total Paid', oldSummary.paidList, newSummary.paidList);
    const received = buildSection('received', 'Total Received', oldSummary.receivedList, newSummary.receivedList);

    const sections = [paid, received];
    const counts = sections.reduce((acc, s) => {
        acc.changed += s.counts.changed;
        acc.added += s.counts.added;
        acc.removed += s.counts.removed;
        return acc;
    }, { changed: 0, added: 0, removed: 0 });

    return { sections, counts };
};

// Human-friendly money display matching the Statement of Taxes table cells. A parenthesised tax
// string is already formatted, so render it as-is; everything else gets 2-decimal + thousands.
export const displayTaxValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string' && value.trim().startsWith('(')) return value.trim();
    if (isNumericLike(value)) {
        return parseFloat(String(value).replace(/,/g, ''))
            .toFixed(2)
            .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
    }
    return String(value);
};
